import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import "./styles/clinicianPortal.css";
import { EmptyState } from "./components/FeedbackStates";
import { ClinicianPortalLayout } from "./components/PortalLayout";
import { PortalDocumentBranding } from "./components/PortalDocumentBranding";
import { ClientProfilePage } from "./pages/ClientProfilePage";
import { AddClientPage } from "./pages/AddClientPage";
import { EditClientPage } from "./pages/EditClientPage";
import { ClinicalTrendsPage } from "./pages/ClinicalTrendsPage";
import {
  DeferredClientPage,
  DeferredPortalPage,
} from "./pages/DeferredPortalPage";
import { ActivityTemplateDetailPage } from "./pages/ActivityTemplateDetailPage";
import { AddActivityTemplatePage } from "./pages/AddActivityTemplatePage";
import { EditActivityTemplatePage } from "./pages/EditActivityTemplatePage";
import { ActivityStepsEditorPage } from "./pages/ActivityStepsEditorPage";
import { ActivityRegistryPage } from "./pages/ActivityRegistryPage";
import { ClinicsPage } from "./pages/ClinicsPage";
import { AddClinicPage } from "./pages/AddClinicPage";
import { EditClinicPage } from "./pages/EditClinicPage";
import { AddTherapistPage } from "./pages/AddTherapistPage";
import { EditTherapistPage } from "./pages/EditTherapistPage";
import { TherapistsPage } from "./pages/TherapistsPage";
import { AdminClientsPage } from "./pages/AdminClientsPage";
import { LovCatalogDetailPage } from "./pages/LovCatalogDetailPage";
import { LovCatalogsPage } from "./pages/LovCatalogsPage";
import { ActivityTemplatesPage } from "./pages/ActivityTemplatesPage";
import { ActivityTemplateOverviewPage } from "./pages/ActivityTemplateOverviewPage";
import { CurrentHomeworkPage } from "./pages/CurrentHomeworkPage";
import { HomeworkActivitiesPage } from "./pages/HomeworkActivitiesPage";
import { AddHomeworkActivityPage } from "./pages/AddHomeworkActivityPage";
import { HomeworkDetailPage } from "./pages/HomeworkDetailPage";
import { HomeworkHistoryPage } from "./pages/HomeworkHistoryPage";
import { ClientSessionsPage } from "./pages/ClientSessionsPage";
import { ClientSessionTranscriptPage } from "./pages/ClientSessionTranscriptPage";
import { MyClientsPage } from "./pages/MyClientsPage";
import { PortalHomePage } from "./pages/PortalHomePage";
import { TeamOverviewPage } from "./pages/TeamOverviewPage";
import { TeamClientsPage } from "./pages/TeamClientsPage";
import { TeamHomeworkPage } from "./pages/TeamHomeworkPage";
import { TeamHomeworkDetailPage } from "./pages/TeamHomeworkDetailPage";
import { TeamReportsPage } from "./pages/TeamReportsPage";
import { PortalSignInPage } from "./pages/PortalSignInPage";
import { ProgressReportsPage } from "./pages/ProgressReportsPage";
import { PracticeCalendarPage } from "./pages/PracticeCalendarPage";
import { UsageEngagementPage } from "./pages/UsageEngagementPage";
import { CopingSkillsPage } from "./pages/CopingSkillsPage";
import { TreatmentPage } from "./pages/TreatmentPage";
import { CaseloadEngagementPage } from "./pages/CaseloadEngagementPage";
import { ValueEngagementPage } from "./pages/ValueEngagementPage";
import { HomeworkOverviewPage } from "./pages/HomeworkOverviewPage";
import { CompletionByDevicePage } from "./pages/CompletionByDevicePage";
import { matchClinicianPortalRoute } from "./routing/portalRoutes";
import { prepareClinicianPortalCacheScope } from "./api/portalRequestCache";
import { ClinicianAssistantProvider } from "./components/ClinicianAssistant";
import { TeamClientWorkspace } from "./components/ClientWorkspace";
import {
  clinicianCodeFromSearch,
  readClinicianCodeSession,
  rememberClinicianCodeSession,
  stripClinicianCodeFromCurrentUrl,
} from "./auth/clinicianSession";

export function isClinicianPortalEnabled(
  value = process.env.REACT_APP_CLINICIAN_PORTAL_ENABLED,
): boolean {
  return value?.trim().toLowerCase() === "true";
}

export default function ClinicianPortalApp() {
  const route = matchClinicianPortalRoute(window.location.pathname);
  const locationSearch = window.location.search;
  const locationClinicianCode = clinicianCodeFromSearch(locationSearch);
  const [sessionClinicianCode, setSessionClinicianCode] = useState(() =>
    readClinicianCodeSession(),
  );
  const clinicianCode = locationClinicianCode || sessionClinicianCode;
  const enabled = isClinicianPortalEnabled();

  useLayoutEffect(() => {
    const search = new URLSearchParams(locationSearch);
    if (!search.has("clinicianCode")) return;

    if (locationClinicianCode) {
      const rememberedClinicianCode = rememberClinicianCodeSession(
        locationClinicianCode,
      );
      setSessionClinicianCode(rememberedClinicianCode);
    }
    stripClinicianCodeFromCurrentUrl();
  }, [locationClinicianCode, locationSearch]);

  const handleSignIn = useCallback((nextClinicianCode: string) => {
    const rememberedClinicianCode =
      rememberClinicianCodeSession(nextClinicianCode);
    setSessionClinicianCode(rememberedClinicianCode);
  }, []);

  useEffect(() => {
    prepareClinicianPortalCacheScope(
      enabled ? clinicianCode : "",
      "supabase",
      "clinician-portal",
    );
  }, [clinicianCode, enabled]);

  return (
    <>
      <PortalDocumentBranding />
      {enabled && clinicianCode ? (
        <ClinicianAssistantProvider
          key={clinicianCode}
          clinicianCode={clinicianCode}
          route={route}
        >
          <ClinicianPortalRouteContent
            route={route}
            clinicianCode={clinicianCode}
            onSignIn={handleSignIn}
          />
        </ClinicianAssistantProvider>
      ) : (
        <ClinicianPortalRouteContent
          route={route}
          clinicianCode={clinicianCode}
          onSignIn={handleSignIn}
        />
      )}
    </>
  );
}

function ClinicianPortalRouteContent({
  route,
  clinicianCode,
  onSignIn,
}: {
  route: ReturnType<typeof matchClinicianPortalRoute>;
  clinicianCode: string;
  onSignIn: (clinicianCode: string) => void;
}) {
  const clientWorkspacePage = (
    patientId: string,
    page: React.ReactElement,
  ): React.ReactElement =>
    "clientSource" in route && route.clientSource === "team" ? (
      <TeamClientWorkspace clinicianCode={clinicianCode} patientId={patientId}>
        {page}
      </TeamClientWorkspace>
    ) : (
      page
    );

  if (!isClinicianPortalEnabled()) {
    return (
      <ClinicianPortalLayout
        activeGlobalKey={route.globalKey}
        clinicianCode={clinicianCode}
      >
        <div className="cp-page-content">
          <EmptyState
            title="Clinician Portal is not enabled"
            description="This React portal is disabled in the current environment."
          />
        </div>
      </ClinicianPortalLayout>
    );
  }

  if (!clinicianCode) {
    return <PortalSignInPage onSignIn={onSignIn} />;
  }

  switch (route.kind) {
    case "clients":
      return <MyClientsPage clinicianCode={clinicianCode} />;
    case "client-new":
      return <AddClientPage clinicianCode={clinicianCode} />;
    case "client-edit":
      return (
        <EditClientPage
          key={route.patientId}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />
      );
    case "client-profile":
      return clientWorkspacePage(
        route.patientId,
        <ClientProfilePage
          key={`${route.clientSource || "assigned"}:${route.patientId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-current-homework":
      return clientWorkspacePage(
        route.patientId,
        <CurrentHomeworkPage
          key={`${route.clientSource || "assigned"}:${route.patientId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-session-transcript":
      return clientWorkspacePage(
        route.patientId,
        <ClientSessionTranscriptPage
          key={`${clinicianCode}:${route.clientSource || "assigned"}:${route.patientId}:${route.sessionId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
          sessionId={route.sessionId}
        />,
      );
    case "client-sessions":
      return clientWorkspacePage(
        route.patientId,
        <ClientSessionsPage
          key={`${clinicianCode}:${route.clientSource || "assigned"}:${route.patientId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-homework-history":
      return clientWorkspacePage(
        route.patientId,
        <HomeworkHistoryPage
          key={`${route.clientSource || "assigned"}:${route.patientId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-homework-detail":
      return clientWorkspacePage(
        route.patientId,
        <HomeworkDetailPage
          key={`${route.patientId}:${route.homeworkAssignmentId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
          homeworkAssignmentId={route.homeworkAssignmentId}
          context={{ kind: "client", primaryKey: route.primaryKey }}
        />,
      );
    case "client-clinical-trends":
      return clientWorkspacePage(
        route.patientId,
        <ClinicalTrendsPage
          key={route.patientId}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-usage-engagement":
      return clientWorkspacePage(
        route.patientId,
        <UsageEngagementPage
          key={route.patientId}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-practice-calendar":
      return clientWorkspacePage(
        route.patientId,
        <PracticeCalendarPage
          key={route.patientId}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-progress-reports":
      return clientWorkspacePage(
        route.patientId,
        <ProgressReportsPage
          key={route.patientId}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
        />,
      );
    case "client-treatment":
      return clientWorkspacePage(
        route.patientId,
        <TreatmentPage
          key={`${route.patientId}:${route.secondaryKey || "plans"}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
          activeKey={route.secondaryKey}
        />,
      );
    case "client-deferred":
      return clientWorkspacePage(
        route.patientId,
        <DeferredClientPage
          key={`${route.patientId}:${route.primaryKey}:${route.secondaryKey || ""}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
          primaryKey={route.primaryKey}
          secondaryKey={route.secondaryKey}
        />,
      );
    case "home":
      return (
        <PortalHomePage key={clinicianCode} clinicianCode={clinicianCode} />
      );
    case "team":
      return <TeamOverviewPage clinicianCode={clinicianCode} />;
    case "team-clients":
      return <TeamClientsPage clinicianCode={clinicianCode} />;
    case "team-homework":
      return <TeamHomeworkPage clinicianCode={clinicianCode} />;
    case "team-homework-detail":
      return (
        <TeamHomeworkDetailPage
          key={`${route.patientId}:${route.homeworkAssignmentId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
          homeworkAssignmentId={route.homeworkAssignmentId}
        />
      );
    case "team-reports":
      return <TeamReportsPage clinicianCode={clinicianCode} />;
    case "activity-templates":
      return <ActivityTemplatesPage clinicianCode={clinicianCode} />;
    case "activity-template-overview":
      return <ActivityTemplateOverviewPage clinicianCode={clinicianCode} />;
    case "activity-template-new":
      return <AddActivityTemplatePage clinicianCode={clinicianCode} />;
    case "activity-registry":
      return <ActivityRegistryPage clinicianCode={clinicianCode} />;
    case "lov-catalogs":
      return <LovCatalogsPage clinicianCode={clinicianCode} />;
    case "lov-options":
      return (
        <LovCatalogDetailPage
          clinicianCode={clinicianCode}
          catalogKey={route.catalogKey}
        />
      );
    case "clinics":
      return <ClinicsPage clinicianCode={clinicianCode} />;
    case "clinic-new":
      return <AddClinicPage clinicianCode={clinicianCode} />;
    case "clinic-edit":
      return (
        <EditClinicPage
          clinicianCode={clinicianCode}
          clinicId={route.clinicId}
        />
      );
    case "therapists":
      return <TherapistsPage clinicianCode={clinicianCode} />;
    case "admin-clients":
      return <AdminClientsPage clinicianCode={clinicianCode} />;
    case "therapist-new":
      return <AddTherapistPage clinicianCode={clinicianCode} />;
    case "therapist-edit":
      return (
        <EditTherapistPage
          clinicianCode={clinicianCode}
          therapistId={route.therapistId}
        />
      );
    case "activity-template-detail":
      return (
        <ActivityTemplateDetailPage
          clinicianCode={clinicianCode}
          templateId={route.templateId}
        />
      );
    case "activity-template-edit":
      return (
        <EditActivityTemplatePage
          clinicianCode={clinicianCode}
          templateId={route.templateId}
        />
      );
    case "activity-template-steps":
      return (
        <ActivityStepsEditorPage
          clinicianCode={clinicianCode}
          source={{ kind: "template", templateId: route.templateId }}
        />
      );
    case "homework-activities":
      return <HomeworkActivitiesPage clinicianCode={clinicianCode} />;
    case "homework-activity-new":
      return <AddHomeworkActivityPage clinicianCode={clinicianCode} />;
    case "coping-skills":
      return <CopingSkillsPage clinicianCode={clinicianCode} />;
    case "caseload-engagement":
      return <CaseloadEngagementPage clinicianCode={clinicianCode} />;
    case "homework-overview":
      return <HomeworkOverviewPage clinicianCode={clinicianCode} />;
    case "completion-by-device":
      return <CompletionByDevicePage clinicianCode={clinicianCode} />;
    case "value-engagement":
      return <ValueEngagementPage clinicianCode={clinicianCode} />;
    case "homework-activity-detail":
      return (
        <HomeworkDetailPage
          key={`${route.patientId}:${route.homeworkAssignmentId}`}
          clinicianCode={clinicianCode}
          patientId={route.patientId}
          homeworkAssignmentId={route.homeworkAssignmentId}
          context={{ kind: "global" }}
        />
      );
    case "homework-activity-steps":
      return (
        <ActivityStepsEditorPage
          key={`${route.patientId}:${route.homeworkAssignmentId}`}
          clinicianCode={clinicianCode}
          source={{
            kind: "homework",
            patientId: route.patientId,
            homeworkAssignmentId: route.homeworkAssignmentId,
          }}
        />
      );
    case "global-deferred":
      return (
        <DeferredPortalPage
          clinicianCode={clinicianCode}
          activeGlobalKey={route.globalKey}
          title={
            route.globalKey === "templates"
              ? "Activity Templates"
              : "Homework Activities"
          }
        />
      );
    case "not-found":
      return (
        <DeferredPortalPage
          clinicianCode={clinicianCode}
          activeGlobalKey={route.globalKey}
          title="Page not found"
        />
      );
  }
}
